import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApis } from '../../../api/useApiClient';
import { useNotification } from "../../../context/NotificationContext";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";

import EditIcon from "@mui/icons-material/Edit";

import LoadingDetailsView from "../Skeletons/LoadingDetailsView";

export default function DetailsView({
  title = "Details",
  subtitleAttribute = null,
  apiPath, // e.g. "/item"
  editPath = null, // The frontend redirect URL to edit the item
  onDelete = null, // optional custom delete handler
  deleteButton = true, // toggle delete button
  // dataLayout = null, // custom data layout component
  children = () => { }, // function that receives data and returns JSX
}) {
  const { tenantApi } = useApis();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const reload = () => setRefresh((prev) => prev + 1);


  const getData = async () => {
    try {
      setLoading(true);
      const res = await tenantApi(apiPath, {
        method: "GET",
      });
      setData(res?.data);
    } catch (e) {
      console.error(e);
      showNotification(
        e.message || `Failed to load ${title}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (apiPath)
      getData();
  }, [apiPath, refresh]);

  if (loading) return <LoadingDetailsView />;


  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          {/* Header with info on left and actions on right */}
          <Box sx={{ 
            display: "flex", 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: "space-between", 
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 2, sm: 0 }
          }}>
            {/* Header info - left */}
            <Box>
              <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subtitleAttribute && data && data[subtitleAttribute] ? data[subtitleAttribute] : null}
              </Typography>
            </Box>
            {/* Header actions - right */}
            {editPath &&
              <Box sx={{ display: "flex", gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(editPath)}
                  fullWidth
                  sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}
                >
                  Edit details
                </Button>
              </Box>
            }
          </Box>

          <Divider />

          {/* Render data layout */}
          {/* {dataLayout ? dataLayout(data) : 'No data layout provided'} */}

          {/* Render children with data */}
          {typeof children === 'function' ? children({ data, reload }) : null}

        </Stack>
      </Paper>
    </Box>
  );
};
